import React, { useCallback, useEffect, useRef, useState } from 'react'
import { ActivityIndicator, Linking, Share, StyleSheet } from 'react-native'

import { useNavigation, useRoute } from '@react-navigation/native'
import { useToast } from '@zet/common/components/toast'
import useCustomerStore from '@zet/customer/src/store'
import {
  B1,
  B2,
  B5,
  Button,
  H5,
  H6,
  Icon,
  SH2,
  SH3,
  Stack,
  Text,
  XStack,
  colors as zetColors,
} from '@zet/design-system'
import Pressable from '@zet/design-system/pressable'
import ArrowLeft from '@zet/design-system/svg/ArrowLeft'
import { BottomView, KeyBoardWrapper } from '@zet/utils'
import LottieView from 'lottie-react-native'
import {
  CodeField,
  Cursor,
  useBlurOnFulfill,
  useClearByFocusCell,
} from 'react-native-confirmation-code-field'

import {
  useGenerateShareLinkforVerifyingOtpToAddCustomer,
  useSendOtpToAddCustomer,
  useVerifyOtpToAddCustomer,
} from '../../api/addCustomer/hooks'
import { bridgeApi } from '../../api/network'
import { i18n } from '../../localization'
import useStore from '../../store'

const { locale = 'en' } = bridgeApi.defaults.headers.common
const EnterOtpScreen = () => {
  const { user } = useStore()
  const [value, setValue] = useState('')
  const [timer, setTimer] = useState<number>(30)
  const ref = useBlurOnFulfill({ value, cellCount: 4 })
  const [isShared, setIsShared] = useState<boolean>(false)
  const { params } = useRoute()
  const navigation = useNavigation()
  const { selectedCustomer } = useCustomerStore()
  const [props, getCellOnLayoutHandler] = useClearByFocusCell({
    value,
    setValue,
  })
  const toast = useToast()
  const handleButtonDisablity = () => {
    if (isShared) return false
    else if (value.length === 4) return false
    else return true
  }

  const interval = 1000
  const timerRef = useRef(0)
  let prev = 0
  let diff = 0
  const timerCallback = () => {
    const date = new Date()
    setTimer((i) => {
      const current = date.getSeconds()
      if (diff === 0) {
        diff = 1
        prev = date.getSeconds()
        return 30
      }

      if (prev > current) diff = 60 - prev + current
      else diff = current - prev

      prev = date.getSeconds()

      if (i > 1)
        if (i + diff < 1) {
          clearInterval(timerRef.current)
          return 1
        } else {
          return i - diff
        }
      else {
        clearInterval(timerRef.current)
        return 0
      }
    })
  }
  const timerFunction = () => {
    timerRef.current = setInterval(timerCallback, interval)
  }

  const otpTimer = useCallback(timerFunction, [timerRef])

  useEffect(() => {
    otpTimer()
    return () => {
      clearInterval(timerRef.current)
    }
  }, [])

  const { mutate: sendOtp } = useSendOtpToAddCustomer()
  const { mutate: generateLink, isLoading: generateLinkLoading } =
    useGenerateShareLinkforVerifyingOtpToAddCustomer()

  const onShare = async (response) => {
    await Share.share({
      message: i18n.t('otpInput.shareDescription', {
        link: response?.data?.link,
        name: user?.name,
      }),
    })
    setIsShared(true)
  }

  const { mutate: verifyOtp } = useVerifyOtpToAddCustomer()
  const handleSubmit = () => {
    verifyOtp(
      {
        customerId: params?.customerId,
        otp: value || '',
        phoneNumber: params?.phoneNumber || selectedCustomer?.phoneNumber,
      },
      {
        onSuccess(data) {
          if (data?.data?.verified) {
            navigation.replace('CustomerBasicDetails', {
              customerId: params?.customerId,
              phoneNumber: params?.phoneNumber || selectedCustomer?.phoneNumber,
              fullName: params?.fullName || selectedCustomer?.name,
            })
          } else if (data?.error) {
            toast.show({
              intent: 'error',
              heading: data?.error?.message,
              hideAfter: 2000,
            })
          }
        },
      }
    )
  }
  return (
    <KeyBoardWrapper
      className="flex-1 bg-white "
      BottomSheet={
        <>
          <Stack className=" justify-end self-center py-3">
            <XStack className="w-full items-center justify-center">
              <Stack className="bg-grays-30 h-[1]   flex-grow"></Stack>
              <B5 className="border-grays-30 rounded-[32] border px-2 py-1">
                {i18n.t('knowCustomer.or')}
              </B5>
              <Stack className="bg-grays-30  h-[1] flex-grow"></Stack>
            </XStack>
            {isShared ? (
              <Button
                textOnly={true}
                className="w-full self-center"
                iconLeft={
                  <Icon
                    type="SecureFilled"
                    color={zetColors.primary[50]}
                    className=" mr-2 "
                  />
                }
                textStyle="text-primary-100"
                onPress={() => setIsShared(false)}
                //   loading={isLoading}
              >
                {i18n.t('otpInput.enter')}
              </Button>
            ) : (
              <Button
                textOnly={true}
                className="w-full self-center"
                iconLeft={
                  generateLinkLoading ? (
                    <ActivityIndicator
                      size="small"
                      color={zetColors.primary[100]}
                    />
                  ) : (
                    <Icon
                      type="Share"
                      color={zetColors.primary[50]}
                      className=" mr-2 "
                    />
                  )
                }
                textStyle="text-primary-100"
                // onPress={() => (isReconsentFlow ? refetchReconsent() : refetch())}
                onPress={() =>
                  generateLink(
                    {
                      customerId: params?.customerId,
                      linkType: 'otpVerify',
                    },
                    { onSuccess: onShare }
                  )
                }
              >
                {i18n.t('customerDetails.shareOtp')}
              </Button>
            )}
          </Stack>
          <BottomView
            stepCount={1}
            maxCount={3}
            onPress={handleSubmit}
            disabled={handleButtonDisablity()}
          />
        </>
      }
    >
      <Stack className="px-3">
        <Stack className="h-3" />

        <Pressable
          className="bg-grays-20 mb-2 h-5 w-5 items-center justify-center rounded"
          onPress={() => navigation.goBack()}
        >
          <ArrowLeft color={zetColors.grays[50]} />
        </Pressable>
        <H5 className="text-grays-100">
          {i18n.t('customerDetails.getPermission')}
        </H5>

        {isShared ? (
          <>
            <Stack className=" mt-9 ">
              <LottieView
                autoPlay
                className=" h-[112] w-[88] self-center "
                source={require('../../../assets/lottie/OTPVerification.json')}
              />
            </Stack>

            <B1 className="text-grays-60 mt-2 ">
              {i18n.t('customerDetails.sentVerificationLink')}

              <H6 className=" text-grays-100 border">
                {' '}
                {params?.phoneNumber || selectedCustomer?.phoneNumber}.{' '}
              </H6>

              {i18n.t('customerDetails.askCustomer')}
            </B1>
            <XStack className=" mt-4  justify-between ">
              <Button
                onPress={() =>
                  Linking.openURL(
                    `tel:${
                      params?.phoneNumber || selectedCustomer?.phoneNumber
                    }`
                  )
                }
                textOnly={true}
                iconLeft={
                  <Icon
                    type="PhoneFilled"
                    color={zetColors.primary[50]}
                    className=" mr-2 "
                  />
                }
                className="px-0"
                textStyle="text-primary-100"
              >
                {i18n.t('customerDetails.call')}
              </Button>
              <Button
                onPress={() =>
                  generateLink(
                    {
                      customerId: params?.customerId,
                      linkType: 'otpVerify',
                    },
                    { onSuccess: onShare }
                  )
                }
                textOnly={true}
                iconLeft={
                  <Icon
                    type="MessageFilled"
                    color={zetColors.primary[50]}
                    className=" mr-2 "
                  />
                }
                className="px-0"
                textStyle="text-primary-100 "
              >
                {i18n.t('customerDetails.sendMessage')}
              </Button>
            </XStack>
          </>
        ) : (
          <>
            <B1 className=" text-grays-60 mt-2">
              {i18n.t('customerDetails.sentOtp')}{' '}
              <H6 className=" text-grays-100 border">
                {params?.phoneNumber || selectedCustomer?.phoneNumber}.{' '}
              </H6>
              {i18n.t('customerDetails.fillOtp')}
            </B1>
            <Stack>
              <CodeField
                ref={ref}
                {...props}
                caretHidden={true}
                value={value}
                onChangeText={(i) => {
                  setValue(i)
                }}
                cellCount={4}
                rootStyle={styles.codeFieldRoot}
                keyboardType="number-pad"
                textContentType="oneTimeCode"
                renderCell={({ index, symbol, isFocused }) => (
                  <Text
                    key={index}
                    style={[styles.cell, isFocused && styles.focusCell]}
                    onLayout={getCellOnLayoutHandler(index)}
                  >
                    {symbol || (isFocused ? <Cursor /> : null)}
                  </Text>
                )}
              />
            </Stack>
            <Stack className="mt-[20] items-center">
              {timer === 0 ? (
                <Pressable
                  opacity={true}
                  onPress={() => {
                    prev = 0
                    diff = 0
                    timerFunction()
                    sendOtp(
                      {
                        phoneNumber:
                          params?.phoneNumber || selectedCustomer?.phoneNumber,
                        fullName: params?.fullName || selectedCustomer?.name,
                      },
                      {
                        onSuccess(data) {
                          if (data?.lockedTill !== null) {
                            toast?.show({
                              intent: 'error',
                              heading: data?.reason,
                              description: data?.lockedTill,
                              hideAfter: 3000,
                            })
                          }
                        },
                      }
                    )
                  }}
                >
                  <SH3 className=" text-primary-100">
                    {i18n.t('customerDetails.resend')}
                  </SH3>
                </Pressable>
              ) : (
                <B2 className=" text-grays-60 text-center">
                  {`Resend OTP in `}
                  <SH3 className="text-primary-100">
                    00:{timer < 10 ? `0${timer}` : timer}
                  </SH3>
                </B2>
              )}
            </Stack>

            <B2 className=" mt-5 ">
              {i18n.t('verifyPhNum.agree')}
              <SH2
                onPress={() =>
                  navigation.navigate('TermsOfUse', {
                    isCustomerFlow: true,
                  })
                }
              >
                {' '}
                {i18n.t('verifyPhNum.terms')}
              </SH2>
              <B2>{i18n.t('verifyPhNum.and')}</B2>
              <SH2
                onPress={() =>
                  navigation.navigate('PrivacyPolicy', {
                    isCustomerFlow: true,
                  })
                }
              >
                {' '}
                {i18n.t('verifyPhNum.privacyPolicy')}.
              </SH2>
              <B2>
                {i18n.t('verifyPhNum.agreeSuffix', {
                  continue:
                    locale === 'hi' ? i18n.t('verifyPhNum.continue') : '',
                })}
              </B2>
            </B2>
          </>
        )}
      </Stack>
    </KeyBoardWrapper>
  )
}

const styles = StyleSheet.create({
  codeFieldRoot: {
    marginTop: 44,
    marginHorizontal: 55,
  },
  cell: {
    width: 52,
    height: 48,
    lineHeight: 47,
    fontSize: 16,
    fontWeight: '500',
    borderWidth: 1,
    borderColor: zetColors.grays[40],
    textAlign: 'center',
    borderRadius: 6,
    paddingHorizontal: 0,
    backgroundColor: '#F9F9FB',
  },
  focusCell: {
    borderColor: zetColors.primary[50],
  },
})

export default EnterOtpScreen
